/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import { jsx } from '@atlaskit/css';
import Suspense from '@atlaskit/react-ufo/suspense';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import { SkillTag } from '@atlassian/rovo-platform-ui-components/skills/SkillTag';

import { SkillMetadataProvider, useSkillMetadata } from '../services/skill-metadata-provider';
export type ConnectedSkillTagProps = {
	/**
	 * The slash command of the skill without the slash (e.g., "jira-issues")
	 * This will be used to look up the skill metadata from the SkillMetadataProvider
	 */
	slug: string;
	/**
	 * Optional flag to invert colors for use with dark backgrounds
	 */
	invertColors?: boolean;
	/**
	 * Optional flag to disable hovercard functionality
	 */
	disableHovercard?: boolean;
	/**
	 * Optional flag to show shimmer effect
	 */
	shimmer?: boolean;
	/**
	 * Optional max-height applied to the hover card content container.
	 * Forwarded to the underlying SkillTag's hovercardMaxHeight prop.
	 */
	hovercardMaxHeight?: number | string;
	/**
	 * The Atlassian site ID for the current context.
	 * Required for fetching skill metadata from the backend.
	 */
	siteId: string;
};

/**
 * Internal component that uses skill metadata from SkillMetadataProvider context.
 * This component must be used within a SkillMetadataProvider to function properly.
 *
 * @internal Use ConnectedSkillTag instead
 */
const ConnectedSkillTagInternal = ({
	slug,
	invertColors,
	disableHovercard,
	shimmer,
	hovercardMaxHeight,
	fallbackTag,
}: Omit<ConnectedSkillTagProps, 'siteId'> & { fallbackTag: React.ReactElement }) => {
	const skillMetadata = useSkillMetadata();

	if (!slug) {
		return null;
	}

	// Get the skill metadata for this slug
	const metadata = skillMetadata?.skillMetadataMap?.[slug];

	// If we don't have metadata, render a basic tag with just the slug
	if (!metadata) {
		return fallbackTag;
	}

	// Render the SkillTag with full metadata
	return (
		<SkillTag
			slug={slug}
			name={metadata.name}
			description={metadata.description}
			iconName={metadata.iconName}
			product={metadata.product}
			productIconUrl={metadata.productIconUrl}
			color={metadata.color}
			invertColors={invertColors}
			disableHovercard={disableHovercard}
			shimmer={shimmer}
			hovercardMaxHeight={hovercardMaxHeight}
		/>
	);
};

/**
 * A connected version of SkillTag that automatically fetches skill metadata
 * from SkillMetadataProvider context based on a slash command.
 *
 * This component wraps ConnectedSkillTagInternal with SkillMetadataProvider
 * to provide skill metadata from the backend.
 *
 * @example
 * ```tsx
 * <ConnectedSkillTag siteId={cloudId} slashCommand="/jira-issues" />
 * ```
 */
export const ConnectedSkillTag = ({
	siteId,
	slug,
	invertColors,
	disableHovercard,
	shimmer,
	hovercardMaxHeight,
}: ConnectedSkillTagProps): JSX.Element => {
	const fallbackTag = (
		<SkillTag slug={slug} name="" invertColors={invertColors} shimmer={shimmer} disableHovercard />
	);

	return (
		<ErrorBoundary
			fallbackRender={() => fallbackTag}
			onError={(error) => {
				logException({
					exception: error,
					name: ExperienceName.HYDRATE_SKILL_METADATA,
					tags: {
						siteId: siteId,
					},
				});
			}}
		>
			{/* TODO: investigate adding a skeleton loader */}
			<AtlassianRelayEnvironmentProvider>
				<Suspense interactionName="rovo-connected-skill-tag" fallback={null}>
					<SkillMetadataProvider siteId={siteId} fetchWithoutTags>
						<ConnectedSkillTagInternal
							slug={slug}
							invertColors={invertColors}
							disableHovercard={disableHovercard}
							shimmer={shimmer}
							hovercardMaxHeight={hovercardMaxHeight}
							fallbackTag={fallbackTag}
						/>
					</SkillMetadataProvider>
				</Suspense>
			</AtlassianRelayEnvironmentProvider>
		</ErrorBoundary>
	);
};
